/**
 * ws.ts — tiny reconnecting WebSocket client. No external deps.
 *
 * - Auto-reconnect with exponential backoff on close.
 * - Buffer outbound frames if not yet open.
 * - Emit parsed JSON frames via an on-message callback.
 *
 * URL is derived from the page origin (same-origin upgrade), or
 * overridden by `VITE_PROCHAT_WS_URL`.
 */
export interface WSClient {
    send: (frame: object) => void;
    close: () => void;
    onmessage: (handler: (frame: unknown) => void) => void;
    onstate:   (handler: (s: WSState) => void) => void;
}

export type WSState = 'connecting' | 'open' | 'closed' | 'error';

export function defaultUrl(): string {
    const env = (import.meta.env.VITE_PROCHAT_WS_URL as string | undefined);
    if (env) return env;
    if (typeof location === 'undefined') return 'ws://127.0.0.1:8086/prochat/ws';
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${location.host}/prochat/ws`;
}

export function connect(url = defaultUrl()): WSClient {
    let ws: WebSocket | null = null;
    let closedByUser = false;
    let backoff = 500;
    const outbox: string[] = [];
    let messageHandler: ((f: unknown) => void) | null = null;
    let stateHandler:   ((s: WSState) => void) | null = null;

    function setState(s: WSState) { stateHandler?.(s); }

    function open() {
        setState('connecting');
        ws = new WebSocket(url);
        ws.onopen = () => {
            backoff = 500;
            setState('open');
            for (const f of outbox) ws!.send(f);
            outbox.length = 0;
        };
        ws.onmessage = (e) => {
            if (!messageHandler) return;
            try { messageHandler(JSON.parse(typeof e.data === 'string' ? e.data : '')); }
            catch { /* ignore malformed */ }
        };
        ws.onerror = () => { setState('error'); };
        ws.onclose = () => {
            setState('closed');
            if (closedByUser) return;
            setTimeout(open, backoff);
            backoff = Math.min(backoff * 2, 8000);
        };
    }
    open();

    return {
        send: (frame) => {
            const s = JSON.stringify(frame);
            if (ws && ws.readyState === ws.OPEN) ws.send(s);
            else outbox.push(s);
        },
        close: () => {
            closedByUser = true;
            try { ws?.close(1000, 'client-close'); }
            catch { /* ignore */ }
        },
        onmessage: (h) => { messageHandler = h; },
        onstate:   (h) => { stateHandler   = h; },
    };
}
